import {
	PlusOutlined,
	SortAscendingOutlined,
	SortDescendingOutlined,
} from "@ant-design/icons"
import { BackButton } from "components/BackButton/BackButton"
import { Badge } from "components/Badge/Badge"
import { Button } from "components/Button/Button"
import { Disclosure } from "components/Disclosure/Disclosure"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Form } from "components/Form/Form"
import { Modal } from "components/Modal/Modal"
import { useNotification } from "components/Notification/Notification"
import { SearchInput } from "components/SearchInput/SearchInput"
import { Segmented } from "components/Segmented/Segmented"
import { Spinner } from "components/Spinner/Spinner"
import { useDisclosure } from "hooks/useDisclosure"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import { Enrollment, EnrollmentCounts } from "interface/enrollment"
import { TemplatedForm } from "modules/dashboard/TemplatedForm"
import { ConsentActions } from "modules/enrollments/ConsentActions"
import { EnrollForm } from "modules/enrollments/EnrollForm"
import { MedicalNecessityFormActions } from "modules/enrollments/MedicalNecessityFormActions"
import { ToggleEnrollment } from "modules/enrollments/ToggleEnrollment"
import { AlternateContactsList } from "modules/patient/AlternateContactsList"
import { ReactNode, useCallback, useMemo, useRef } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import {
	useCancelEnrollment,
	useEnrollments,
	useEnrollmentsCounts,
	useEnrollPatient,
	useMedicalNecessityFormTemplate,
	useSaveMedicalNecessityFormAnswers,
} from "services/enrollments"
import { DEFAULT_PLACEHOLDER } from "utils/constants"
import { dateFormatter, formatDate } from "utils/date"
import { prettyprintPhoneNumber } from "utils/patient"

export const Enrollments = () => {
	const { scopeId } = useParams()
	const navigate = useNavigate()
	const targetScroll = useRef<HTMLUListElement>(null)
	const discloseForm = useDisclosure<Enrollment>()
	const [searchParams, setSearchParams] = useSearchParams(DEFAULT_SEARCH_PARAMS)
	const query = searchParams.get("query") ?? ""
	const order = Number(searchParams.get("order") ?? -1)
	const status = useMemo(
		() =>
			(searchParams.get("status")
				? searchParams.get("status")!.split(",")
				: ["Pending"]) as Array<Enrollment["status"]>,
		[searchParams],
	)
	const { notify } = useNotification()
	const { data: counts } = useEnrollmentsCounts()
	const {
		data: { pages: enrollments = [] } = {},
		hasNextPage,
		fetchNextPage,
		isFetching,
		isFetchingNextPage,
	} = useEnrollments({
		order,
		status,
		fullName: query,
	})
	const isLoading = isFetching || isFetchingNextPage
	const toggleOptions = useMemo(() => buildToggleOptions(counts), [counts])
	const selectedToggleOption = useMemo(
		() =>
			toggleOptions.find((statuses) =>
				statuses.value.every((s) => status.includes(s)),
			),
		[status, toggleOptions],
	)
	const SortIcon = order === -1 ? SortDescendingOutlined : SortAscendingOutlined
	const sortLabel = `Sort by ${status.includes("Installation date set") ? "Installation" : "Request"} date`
	const handleChangeStatus = useCallback(
		(value: Array<Enrollment["status"]>) => {
			targetScroll.current?.scrollTo({ top: 0 })
			setSearchParams((previousParams) => ({
				...Object.fromEntries(previousParams.entries()),
				status: value.join(","),
				order: String(value.includes("Installation date set") ? "1" : "-1"),
			}))
		},
		[setSearchParams],
	)
	const handleSuccess = () => {
		setSearchParams((previousParams) => ({
			...Object.fromEntries(previousParams.entries()),
			status: "Pending",
		}))
	}
	const handleSort = () => {
		setSearchParams((previousParams) => ({
			...Object.fromEntries(previousParams.entries()),
			order: String(order * -1),
		}))
	}

	useInfiniteScroll({
		target: targetScroll.current,
		hasMore: hasNextPage,
		isLoading,
		onLoadMore: fetchNextPage,
	})

	return (
		<Spinner spinning={isLoading}>
			<div className="mx-auto flex max-h-full max-w-300 flex-col gap-4 overflow-hidden">
				<BackButton onClick={() => navigate(`/${scopeId}`)}>
					All patients
				</BackButton>

				<div className="flex w-full flex-col gap-3 overflow-hidden">
					<div className="flex justify-between">
						<h2 className="font-nunito text-3xl font-semibold">
							Pending enrollments
						</h2>
						<EnrollPatient onSuccess={handleSuccess} />
					</div>

					<Segmented<Array<Enrollment["status"]>>
						aria-label="Status"
						options={toggleOptions}
						value={selectedToggleOption?.value}
						onChange={handleChangeStatus}
					/>

					<div className="flex w-full gap-1">
						<SearchInput
							value={query}
							placeholder="Search patients"
							disabled={isLoading}
							onSearch={(query) =>
								setSearchParams((previousParams) => ({
									...Object.fromEntries(previousParams.entries()),
									query,
								}))
							}
						/>
						{enrollments.length > 0 && (
							<Button
								icon={<SortIcon />}
								className="md:hidden"
								aria-label={sortLabel}
								disabled={isLoading}
								onClick={handleSort}
							/>
						)}
					</div>

					<div className="flex flex-col gap-4 overflow-hidden">
						<div className="hidden gap-2 rounded-lg bg-mint-0 p-5 py-3 md:flex">
							<div className="grid w-full grid-cols-4 items-center gap-2">
								<div
									role="columnheader"
									className="font-bold whitespace-break-spaces"
								>
									Patient full name
								</div>
								<div
									role="columnheader"
									className="flex gap-2 font-bold whitespace-break-spaces"
								>
									Request date
									{!status.includes("Installation date set") &&
										enrollments.length > 0 && (
											<Button
												size="small"
												type="dashed"
												icon={<SortIcon />}
												aria-label={sortLabel}
												onClick={handleSort}
											/>
										)}
								</div>
								<div
									role="columnheader"
									className="font-bold whitespace-break-spaces"
								>
									Medical necessity form
								</div>
								<div
									role="columnheader"
									className="flex gap-2 font-bold whitespace-break-spaces"
								>
									{status.includes("Installation date set") ? (
										<>
											Installation date
											<Button
												size="small"
												type="dashed"
												icon={<SortIcon />}
												aria-label={sortLabel}
												onClick={handleSort}
											/>
										</>
									) : (
										"Action"
									)}
								</div>
							</div>
							<div className="invisible size-3.5" />
						</div>

						<ul
							ref={targetScroll}
							className="flex flex-col gap-4 overflow-y-auto"
							aria-label={
								isLoading
									? "Waiting for enrollments"
									: selectedToggleOption?.title
							}
						>
							{enrollments.map((enrollment) => {
								const { id, fullName, installationDate, createdAt } = enrollment
								const details = buildPatientDetails(enrollment)

								return (
									<li key={id} aria-label={`Patient ${fullName}`}>
										<Disclosure
											name="enrollments"
											summary={
												<div className="flex w-full flex-col items-center gap-2 md:grid md:grid-cols-4">
													<div className="flex w-full justify-between gap-2 wrap-anywhere">
														<span className="text-navy-60 md:hidden">
															Patient full name:
														</span>
														<span className="font-bold">{fullName}</span>
													</div>

													<div className="flex w-full justify-between gap-2">
														<span className="text-navy-60 md:hidden">
															Request date:
														</span>
														<time dateTime={createdAt} className="text-navy-60">
															{dateFormatter(createdAt)}
														</time>
													</div>

													<div className="flex w-full justify-between gap-2">
														<span className="text-navy-60 md:hidden">
															Medical necessity form:
														</span>
														<MedicalNecessityFormStatusAction
															enrollment={enrollment}
															onFillOut={discloseForm.open}
														/>
													</div>

													<div className="flex w-full justify-between gap-2">
														{installationDate ? (
															<>
																<span className="text-navy-60 md:hidden">
																	Installation date:
																</span>
																<time
																	dateTime={installationDate}
																	className="text-navy-60"
																>
																	{dateFormatter(installationDate)}
																</time>
															</>
														) : (
															<>
																<span className="text-navy-60 md:hidden">
																	Action:
																</span>
																<Cancel enrollment={enrollment} />
															</>
														)}
													</div>
												</div>
											}
										>
											<dl
												className="columns-xs space-y-2 text-navy-100"
												aria-label={`Enrollment details for ${fullName}`}
											>
												{details.map(([title, description]) => (
													<dt
														key={title as string}
														className="flex items-start gap-2"
													>
														<span className="text-nowrap text-navy-60">
															{title}:
														</span>
														{description}
													</dt>
												))}
											</dl>
										</Disclosure>
									</li>
								)
							})}
						</ul>
					</div>
				</div>
			</div>

			{discloseForm.isOpen && discloseForm.value && (
				<CreateMedicalNecessityForm
					enrollment={discloseForm.value}
					onClose={discloseForm.close}
					onSuccess={() => {
						notify.success({
							message: "Medical necessity form",
							description: "Answers saved successfully",
						})
						handleSuccess()
						discloseForm.close()
					}}
				/>
			)}
		</Spinner>
	)
}

function EnrollPatient({ onSuccess }: { onSuccess: VoidFunction }) {
	const { notify } = useNotification()
	const disclose = useDisclosure()
	const enroll = useEnrollPatient()

	return (
		<>
			<Button
				type="primary"
				icon={<PlusOutlined aria-hidden />}
				onClick={disclose.open}
			>
				Enroll patient
			</Button>

			{disclose.isOpen && (
				<EnrollForm
					{...enroll}
					open
					onClose={disclose.close}
					onSuccess={() => {
						notify.success({
							message: "Enroll patient",
							description: "Patient enrollment successful",
						})
						onSuccess()
						disclose.close()
					}}
				/>
			)}
		</>
	)
}

function Cancel({ enrollment }: { enrollment: Enrollment }) {
	const disclose = useDisclosure()
	const cancel = useCancelEnrollment()

	return (
		<>
			<Button
				type="link"
				className="size-fit justify-start px-0 text-yellow-orange-60"
				onClick={disclose.open}
			>
				Cancel enrollment
			</Button>

			{disclose.isOpen && (
				<ToggleEnrollment
					{...cancel}
					open
					enrollment={enrollment}
					onCancel={disclose.close}
					onSuccess={disclose.close}
				/>
			)}
		</>
	)
}

const buildToggleOptions = (
	counts?: EnrollmentCounts,
): {
	value: Array<Enrollment["status"]>
	label: ReactNode
	title: string
}[] => {
	const installationDateSetCount = counts?.["Installation date set"] || 0
	const awaitingCount = counts?.Pending || 0
	const pendingcount =
		(counts?.["Ready for fulfillment"] || 0) + (counts?.["Form completed"] || 0)
	return [
		{
			value: ["Pending"],
			title: `Awaiting medical necessity${awaitingCount > 0 ? ` (${awaitingCount})` : ""}`,
			label: (
				<>
					Awaiting medical necessity
					{awaitingCount ? (
						<Badge
							count={awaitingCount}
							classNames={{ indicator: "ml-1 bg-red-40" }}
						/>
					) : null}
				</>
			),
		},
		{
			value: ["Form completed", "Ready for fulfillment"],
			title: `Enrollment pending${pendingcount > 0 ? ` (${pendingcount})` : ""}`,
			label: `Enrollment pending${pendingcount > 0 ? ` (${pendingcount})` : ""}`,
		},
		{
			value: ["Installation date set"],
			title: `Installation scheduled${installationDateSetCount > 0 ? ` (${installationDateSetCount})` : ""}`,
			label: `Installation scheduled${installationDateSetCount > 0 ? ` (${installationDateSetCount})` : ""}`,
		},
	]
}

const MedicalNecessityFormStatusAction = ({
	enrollment,
	onFillOut,
}: {
	enrollment: Enrollment
	onFillOut: (enrollment: Enrollment) => void
}) => {
	switch (enrollment.status) {
		case "Pending":
			return (
				<Button
					type="link"
					className="size-fit justify-start px-0"
					onClick={() => {
						onFillOut(enrollment)
					}}
				>
					Fill out the form
				</Button>
			)
		case "Form completed":
		case "Ready for fulfillment":
		case "Installation date set":
			return <MedicalNecessityFormActions enrollment={enrollment} />
		default:
			return <div />
	}
}

const CreateMedicalNecessityForm = ({
	enrollment,
	onClose,
	onSuccess,
}: {
	enrollment: Enrollment
	onClose: () => void
	onSuccess: () => void
}) => {
	const [form] = Form.useForm()
	const { data: questions = [], isFetching } = useMedicalNecessityFormTemplate()
	const {
		mutateAsync: saveAnswers,
		isPending,
		isError,
	} = useSaveMedicalNecessityFormAnswers()

	return (
		<Modal
			open
			title="Medical necessity form"
			okType="primary"
			okText="Submit"
			cancelText="Cancel"
			loading={isFetching}
			confirmLoading={isPending}
			onOk={form.submit}
			onCancel={onClose}
			classNames={{
				body: "flex flex-col gap-2",
			}}
		>
			<TemplatedForm
				form={form}
				questions={questions}
				explainPlaceholder="Describe..."
				onFinish={async (answers) => {
					await saveAnswers({ id: enrollment.id, answers })
					onSuccess()
				}}
			/>
			<ErrorStatus show={isError}>
				An unexpected error occurred while saving answers. Please, try again.
			</ErrorStatus>
		</Modal>
	)
}

const buildPatientDetails = (enrollment: Enrollment) => [
	["Address", enrollment.address],
	["Date of birth", formatDate(enrollment.dateOfBirth)],
	["Biological sex", enrollment.biologicalSex],
	["Phone number", prettyprintPhoneNumber(enrollment.phoneNumber)],
	["Email", enrollment.email ?? DEFAULT_PLACEHOLDER],
	["Medical record number", enrollment.medicalRecordNumber],
	[
		"Last recorded weight",
		enrollment.lastRecordedWeightPounds
			? `${enrollment.lastRecordedWeightPounds} lbs`
			: DEFAULT_PLACEHOLDER,
	],
	[
		"Insurance",
		enrollment.insurances?.map(({ name, custom }) => custom ?? name).join(", "),
	],
	["Signed consent form", <ConsentActions enrollment={enrollment} />],
	["Notes", <span className="whitespace-pre-line">{enrollment.notes}</span>],
	[
		"Alternate contacts",
		<AlternateContactsList
			alternateContacts={enrollment.alternateContacts}
			permission="view"
		/>,
	],
]

const DEFAULT_SEARCH_PARAMS = {
	query: "",
	order: "-1",
	status: "Pending",
}
